<template>
  <div>
    <multiselect
      class="my-multiselect"
      :value="selectedTeamObject"
      @input="handleTeamSelect"
      :placeholder="$t('donor_dashboard.select_team', 'Select team')"
      label="displayName"
      track-by="id"
      :options="teamOptions"
      :show-labels="true"
      :allow-empty="true"
      :disabled="!teamOptions || teamOptions.length === 0 || loading"
    >
    </multiselect>
  </div>
</template>

<script setup>
import multiselect from "vue-multiselect";
import { computed, ref, watch } from "vue";
import { useProctectedMyTeamsList } from "@/views/dashboard/compositions/my-teams";
import { useTeamLeaderContacts } from "@/composables/useDashboardTeamLeaderContacts";

const loading = ref(false);

const {
  campaignId: cId,
  fetchContacts,
  storeSearchQuery,
  getSearchFiltersURLParams,
  storeSearchFilters,
  teamId: storeTeamId,
} = useTeamLeaderContacts();

const { teams, fetchProtectedMyTeams } = useProctectedMyTeamsList();

function filterTeamsByCampaignId(teams, campaignId) {
  return teams
    .filter((team) => Number(team?.attributes?.campaign_id) === Number(campaignId))
    .map((team) => team);
}

const getTeams = async () => {
  loading.value = true;
  try {
    await fetchProtectedMyTeams();
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

watch(
  cId,
  () => {
    getTeams();
  },
  { immediate: true }
);

const teamOptions = computed(() => {
  return teamsBySelectedCampaign.value.map((team) => ({
    id: team.id,
    displayName: `(${team.id}) ${team.attributes.name}`,
  }));
});

const selectedTeamObject = computed(() => {
  if (!storeTeamId.value) return null;
  return teamOptions.value.find((option) => option.id == storeTeamId.value);
});

const handleTeamSelect = async (selectedOption) => {
  loading.value = true;
  const teamId = selectedOption ? selectedOption.id : null;
  storeTeamId.value = teamId;
  const params = getSearchFiltersURLParams(storeSearchFilters.value);
  await fetchContacts({
    campaign_id: cId,
    ...(teamId ? { team_id: teamId } : {}),
    ...(storeSearchQuery.value ? { search: storeSearchQuery.value } : {}),
    ...(params.status.length > 0 ? { status: params.status } : {}),
  });
  loading.value = false;
};

const teamsBySelectedCampaign = computed(() => {
  return filterTeamsByCampaignId(teams.value, cId.value);
});
</script>

<style lang="scss" scoped>
::v-deep .multiselect.my-multiselect {
  @apply tw-w-full md:tw-w-[276px] tw-rounded-full;

  .multiselect__tags {
    @apply tw-flex tw-items-center tw-min-h-[55px] md:tw-min-h-[55px] tw-ps-[24px] tw-pe-[48px] md:tw-ps-[44px] md:tw-px-[52px] tw-rounded-full tw-border-[var(--my-white-soft)] tw-bg-[var(--my-white-soft)] tw-cursor-pointer;
  }

  .multiselect__select {
    @apply tw-top-[50%] tw-right-[8px] md:tw-right-[16px] tw-translate-y-[-50%];

    &:before {
      @apply tw-border-t-[var(--my-primary)];
    }
  }

  &.multiselect--active .multiselect__select {
    @apply tw-top-[50%];
    transform: rotateZ(180deg) translateY(50%);
  }

  .multiselect__content-wrapper {
    @apply tw-rounded-[20px];
  }

  .multiselect__single,
  .multiselect__input,
  .multiselect__placeholder {
    @apply tw-text-[18px] tw-text-[var(--my-text-color)] tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-bg-[var(--my-white-soft)] tw-border-[var(--my-white-soft)] tw-cursor-pointer;
  }

  .multiselect__element .multiselect__option {
    @apply tw-block tw-truncate;
  }

  .multiselect__option--highlight {
    @apply tw-bg-[var(--my-primary)];
  }

  .multiselect__spinner {
    @apply tw-top-[50%] tw-right-[8px] md:tw-right-[16px] tw-translate-y-[-50%] tw-bg-[var(--my-white-soft)];
  }

  &.multiselect--disabled {
    .multiselect__select {
      @apply tw-bg-[var(--my-white-soft)];
    }
  }
}
</style>
